import { Box, Button, Flex, Icon, Spinner, Text, Tooltip } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { AiOutlineInstagram } from 'react-icons/ai';
import { IoLogoFacebook } from 'react-icons/io';
import { IoPhonePortrait, IoSquareOutline } from "react-icons/io5";
import { MdFacebook, MdOutlinePhonelink } from 'react-icons/md';
import { RiMessengerLine } from "react-icons/ri";
import { TbRectangle, TbRectangleVertical } from "react-icons/tb";
import { useGetAdPreviewsQuery } from 'store/campaigns/adsApi';
import adFormatMapping from '../../utils/adFormatMapping.json';
import placementMapping from '../../utils/placementMapping.json';
import { useGetInstagramByPageIDQuery } from 'store/BmAccounts/bmAccountApi';
import { removeEmptyValues } from 'utils/functions';

const iconMapping = {
    "AiOutlineInstagram": <AiOutlineInstagram />,
    "MdFacebook": <MdFacebook />,
    "RiMessengerLine": <RiMessengerLine />,
    "IoLogoFacebook": <IoLogoFacebook />,
    "IoPhonePortrait": <IoPhonePortrait />
};

const AdFormatDisplay = ({ ad }) => {
    const adFormat = adFormatMapping[ad.format] || { name: 'No Format', icon: null };
    
    return (
      <Flex alignItems="center" gap={2}>
        {/* Display the icon */}
        <Box>
          {iconMapping[adFormat.icon] || null}
        </Box>
  
        {/* Display the ad format name */}
        <Text py={4} fontSize={'12px'}>
          {adFormat.name || 'No Format'}
        </Text>
      </Flex>
    );
  };


// Mapping function to get the selected placements based on platform type
const getSelectedPlacements = (platformType) => {
    switch (platformType) {
        case 'all':
            return [
                "INSTAGRAM_REELS",
                "INSTAGRAM_STORY",
                "INSTAGRAM_EXPLORE_CONTEXTUAL",
                "INSTAGRAM_PROFILE_REELS",
                "INSTAGRAM_PROFILE_FEED",
                "MOBILE_FEED_BASIC",
                "FACEBOOK_REELS_MOBILE",
                "INSTAGRAM_REELS_OVERLAY",
                "FACEBOOK_PROFILE_FEED_MOBILE",
                "SUGGESTED_VIDEO_MOBILE",
                "INSTREAM_VIDEO_MOBILE",
                "MARKETPLACE_MOBILE",
                "FACEBOOK_STORY_MOBILE",
                "MESSENGER_MOBILE_INBOX_MEDIA",
                "MESSENGER_MOBILE_STORY_MEDIA"
            ];
        case 'tab':
            return [
                "INSTAGRAM_SEARCH_GRID",
                "INSTAGRAM_EXPLORE_CONTEXTUAL",
                "INSTAGRAM_PROFILE_REELS",
                "INSTAGRAM_EXPLORE_GRID_HOME",
                "INSTAGRAM_PROFILE_FEED",
                "MOBILE_FEED_BASIC",
                "SUGGESTED_VIDEO_MOBILE",
                "MARKETPLACE_MOBILE",
                "MESSENGER_MOBILE_INBOX_MEDIA"
            ];
        case 'mobile':
            return [
                "INSTAGRAM_REELS",
                "INSTAGRAM_STORY",
                "FACEBOOK_REELS_MOBILE",
                "FACEBOOK_PROFILE_FEED_MOBILE",
                "INSTREAM_VIDEO_MOBILE",
                "FACEBOOK_STORY_MOBILE",
                "MOBILE_INTERSTITIAL",
                "MESSENGER_MOBILE_STORY_MEDIA"
            ];
        case 'desktop':
            return [
                "RIGHT_COLUMN_STANDARD",
                "MARKETPLACE_SEARCH_ADS_MOBILE"
            ];
        default:
            return [];
    }
};

function CatalogueAdPreview({file, creatives, adData }) {

    const { adsAccount } = creatives;
    const { targeting: placements, promoted_object, destination_type } = creatives?.adsets[0];
    const [selectedPlacements, setSelectedPlacements] = useState([]);
    const [creative, setCreative] = useState(null);
    const [selectedPlatform, setSelectedPlatform] = useState('all');    
    const filterCreative = removeEmptyValues(creative);
    const updatedCreative = encodeURIComponent(JSON.stringify(filterCreative));

    const matchingadData = adData?.adsAccounts?.find(
        (obj) => obj.adsAccountId === adsAccount.adsAccountId
    );

    const { data: AdPreview, isLoading, error } = useGetAdPreviewsQuery(
        {
            adsAccountId: adsAccount?.adsAccountId,
            creative: updatedCreative,
            ad_format: selectedPlacements, // Passing selected placements to ad_format API
        },
        { skip: !updatedCreative || !adsAccount?.adsAccountId || !creative || selectedPlacements.length === 0 }
    );

    // Fallback query — only triggers if instagram_user_id is missing
    const shouldFetchInstagramActorId = !matchingadData?.instagram_user_id && matchingadData?.page_id;

    const { data: pageBackedInstaAccounts, isLoading: isFacebookIdLoading, isFetching:isFacebookIdFetching } = useGetInstagramByPageIDQuery(
        { pageId: matchingadData?.page_id },
        { skip: !shouldFetchInstagramActorId }
    );

    // Constructing the creative object from the file data
    useEffect(() => {
        if (file && creatives) {
            let creative = {};

            if(creatives?.force_single_link === 'true'){
                const templateUrlSpec = creatives?.applink_treatment === 'web_only'
                    ? {
                        web: {
                            url: file?.template_url_spec?.web?.url || '',
                        },
                    }
                    : {
                        web: {
                            url: file?.template_url_spec?.web?.url || '',
                        },
                        ios: {
                            url: file?.template_url_spec?.ios?.url || '',
                        },
                        android: {
                            url: file?.template_url_spec?.android?.url || '',
                        },
                        config: {
                            app_id: file?.template_url_spec?.config?.app_id || '',
                        },
                    };

                creative = {
                    name: file?.name,
                    object_type: 'SHARE',
                    product_set_id: file?.product_set_id,
                    applink_treatment: creatives?.applink_treatment,
                    template_url_spec: templateUrlSpec,
                    object_story_spec: {
                        template_data: {
                            name: file?.name,
                            message: file?.message,
                            description: file?.description,
                            link: file?.link,
                            call_to_action: {
                                type: file?.type,
                            },
                            image_layer_specs: file?.image_layer_specs,
                        },
                        instagram_user_id: matchingadData?.instagram_user_id || pageBackedInstaAccounts?.connectedAccount?.[0]?.id || pageBackedInstaAccounts?.page_backed_instagram_accounts?.data[0]?.id,
                        page_id: matchingadData?.page_id,
                    },
                    contextual_multi_ads: {
                        enroll_status : file?.contextual_multi_ads?.enroll_status
                    },
                    // degrees_of_freedom_spec: {
                    //     creative_features_spec: {
                    //         standard_enhancements: {
                    //             enroll_status: 'OPT_OUT',
                    //         },
                    //     },
                    // },
                }
            }else{
                const templateUrlSpec = creatives?.applink_treatment === 'web_only'
                    ? {
                        web: {
                            url: file?.template_url_spec?.web?.url || '',
                        },
                    }
                    : {
                        web: {
                            url: file?.template_url_spec?.web?.url || '',
                        },
                        ios: {
                            url: file?.template_url_spec?.ios?.url || '',
                        },
                        android: {
                            url: file?.template_url_spec?.android?.url || '',
                        },
                        config: {
                            app_id: file?.template_url_spec?.config?.app_id || '',
                        },
                };

                creative = {
                    name: file?.name,
                    object_type: 'SHARE',
                    product_set_id: file?.adcreatives[0]?.product_set_id,
                    applink_treatment: file?.applink_treatment,
                    template_url_spec: templateUrlSpec,
                    object_story_spec: {
                        template_data: {
                            name: file?.name,
                            message: file?.message,
                            description: file?.description,
                            caption: file?.caption,
                            link: file.link,
                            call_to_action: {
                                type: file?.type,
                            },
                            format_option: file?.adcreatives[0]?.format_option,
                            image_layer_specs: file?.adcreatives[0]?.image_layer_specs
                        },
                        instagram_user_id: matchingadData?.instagram_user_id || pageBackedInstaAccounts?.connectedAccount?.[0]?.id || pageBackedInstaAccounts?.page_backed_instagram_accounts?.data[0]?.id,
                        page_id: matchingadData?.page_id,
                    },
                    message: file?.message,
                    asset_feed_spec: {
                        ad_formats: [
                            "CAROUSEL",
                            "COLLECTION"
                        ],
                        optimization_type: "FORMAT_AUTOMATION",
                    },
                    contextual_multi_ads: {
                        enroll_status : file?.contextual_multi_ads?.enroll_status
                    },
                    // degrees_of_freedom_spec: {
                    //     creative_features_spec: {
                    //         standard_enhancements: {
                    //             enroll_status: 'OPT_OUT',
                    //         },
                    //     },
                    // },
                }
            }

            setCreative(creative);
        }
    }, [file, matchingadData, creatives]);

    // Helper function to unescape the string
    const unescapeHtml = (str) => {
        const string = str.replace(/\\"/g, '"').replace(/\\'/g, "'").replace(/\\\\/g, '\\');
        return string;
    };

    // Helper function to map raw positions to placement names
    const mapPositionsToPlacements = (platform, positions) => {
        const platformMapping = placementMapping[platform];
        return positions.map(position => platformMapping[position]);
    };

    // Handle icon button clicks to set the selected placements
    const handleIconClick = (platformType) => {
        setSelectedPlatform(platformType);

        // Get the raw positions from the parent
        const rawPositions = {
            facebook: placements?.facebook_positions || [],
            instagram: placements?.instagram_positions || [],
            audienceNetwork: placements?.audience_network_positions || [],
            messenger: placements?.messenger_positions || [],
        };

        // If rawPositions is empty (i.e., no positions are available), fallback to all placements
        if (!rawPositions.facebook.length && !rawPositions.instagram.length && !rawPositions.audienceNetwork.length && !rawPositions.messenger.length) {
            // Fallback to default "all" placements
            rawPositions.facebook = Object.keys(placementMapping.facebook);
            rawPositions.instagram = Object.keys(placementMapping.instagram);
            rawPositions.audienceNetwork = Object.keys(placementMapping.audienceNetwork);
            rawPositions.messenger = Object.keys(placementMapping.messenger);
        }

        // Map the raw positions to their mapped values
        const mappedPlacements = [
            ...mapPositionsToPlacements('instagram', rawPositions.instagram),
            ...mapPositionsToPlacements('facebook', rawPositions.facebook),
            ...mapPositionsToPlacements('audienceNetwork', rawPositions.audienceNetwork),
            ...mapPositionsToPlacements('messenger', rawPositions.messenger),
        ];

        // Get the selected placements based on platformType
        const newPlacements = getSelectedPlacements(platformType);

        // Only filter out the specific placements if video data exists
       let filteredPlacements = mappedPlacements.filter(placement => newPlacements.includes(placement));

       if (file?.adcreative?.video_id) {
           // Filter out the unwanted placements when video_data is present
           filteredPlacements = filteredPlacements.filter(
               placement => placement !== 'INSTAGRAM_REELS_OVERLAY' && placement !== 'INSTAGRAM_SEARCH_GRID'
           );
       }

        setSelectedPlacements(filteredPlacements); // Update the selected placements based on intersection
    };

    useEffect(() => {
        // Set initial placements based on the 'all' selection
        if (selectedPlatform === 'all') {
            handleIconClick('all');
        }
    }, [selectedPlatform, placements]);

    return (
        <Box>
            {/* Preview icons (All, Tablet, Mobile, Desktop) */}
            <Flex justifyContent="center" gap={6} my={4} wrap="wrap">
                <Tooltip label="All" fontSize="sm" placement="top">
                    <Button
                        onClick={() => handleIconClick('all')}
                        aria-label="All"
                        variant="solid"
                        boxSize={6}
                        colorScheme='brand'
                        bg={selectedPlatform === 'all' ? 'brand.800' : 'gray.400'}
                    >
                        <Icon as={MdOutlinePhonelink} boxSize={4} />
                    </Button>
                </Tooltip>
                <Tooltip label="Feeds, In-stream ads for videos and reels, Search results" fontSize="sm" placement="top">
                    <Button
                        onClick={() => handleIconClick('tab')}
                        aria-label="Tablet"
                        variant="solid"
                        boxSize={6}
                        colorScheme='brand'
                        bg={selectedPlatform === 'tab' ? 'brand.800' : 'gray.400'}
                    >
                        <Icon as={IoSquareOutline} boxSize={4} />
                    </Button>

                </Tooltip>
                <Tooltip label="Stories and Reels, Apps and sites" fontSize="sm" placement="top">
                    <Button
                        onClick={() => handleIconClick('mobile')}
                        aria-label="Mobile"
                        variant="solid"
                        boxSize={6}
                        colorScheme='brand'
                        bg={selectedPlatform === 'mobile' ? 'brand.800' : 'gray.400'}
                    >
                        <Icon as={TbRectangleVertical} boxSize={4} />
                    </Button>

                </Tooltip>
                <Tooltip label="A Right column, Search results" fontSize="sm" placement="top">
                    <Button
                        onClick={() => handleIconClick('desktop')}
                        aria-label="Desktop"
                        variant="solid"
                        boxSize={6}
                        colorScheme="brand"
                        bg={selectedPlatform === 'desktop' ? 'brand.800' : 'gray.400'}
                    >
                        <Icon as={TbRectangle} boxSize={4} />
                    </Button>

                </Tooltip>
            </Flex>

            {/* Ad Preview */}
            {isLoading ? (
                <Flex justifyContent={'center'} alignItems={'center'} height={'60vh'}>
                    <Spinner />
                </Flex>
            ) : error ? (
                <Box>Error loading ad preview!</Box>
            ) : AdPreview && AdPreview.length > 0 ? (
                <Box display="flex" flexWrap="wrap" overflowY={'auto'}>
                    {AdPreview.map((ad, index) => (
                        <Box key={index} width="50%">
                            <Box>
                                 <Flex alignItems="center" gap={2}>
                                    <AdFormatDisplay ad={ad}/>
                                </Flex>
                                {ad.body ? (
                                    <Box
                                        dangerouslySetInnerHTML={{ __html: unescapeHtml(ad.body) }}
                                        width="300px"
                                        height="560px"
                                        bg="#EFEFEF"
                                        boxShadow="0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)"
                                        display="flex"
                                        flexDirection="column"
                                        justifyContent="center"
                                        alignItems="center"
                                        borderRadius="8px"
                                        className='ad-preview-wrapper'
                                    />
                                ) : (
                                    <Box>No preview available for this format.</Box>
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>
            ) : (
                <Box h={'500px'} display="flex" alignItems={'center'} justifyContent={'center'}>
                    Add media to see ad examples.
                </Box>
            )}
        </Box>
    );
}

export default CatalogueAdPreview;
